import monitoringController from "@/services/monitoringService";
import documentChecklistController from "@/services/legalDocumentService";
import Swal from "sweetalert2";
import Multiselect from "@vueform/multiselect";
import corporateController from "@/services/corporateService";
import partnerTypeController from "@/services/partnerTypeService";
import parameterController from "@/services/parameterService";
import "@vueform/multiselect/themes/default.css";

export default {
  name: "DocumentFormView",
  components: { Multiselect },

  props: {
    mode: {
      type: String,
      required: true,
      validator: (v) => ["add", "edit", "detail"].includes(v),
    },
  },

  data() {
    return {
      corporateList: [],
      partnerTypeList: [],
      descriptionList: [],
      statusList: [],
      inactiveReasonList: [],
      progressList: [],
      renewalList: [],
      companyList: [],
      filteredDescriptionList: [],
      timelineList: [],
      inactiveReasonSelect: null,
      prevCandidates: [],
      originalPrevDocumentId: null,

      form: this.getEmptyForm(),
      refDocumentNumber: "",

      detail: {},
      parentDetail: null,
      addendumList: [],
      originalProgressStatusName: "",
      originalRenewalTypeName: "",

      checklist: this.getEmptyChecklist(),
      checklistPendingFiles: {},
      checklistDragging: null,

      isLoading: false,
      isFormReady: false,
      isDragging: false,

      toast: { show: false, message: "", type: "success" },
      toastTimer: null,
    };
  },

  computed: {
    isEditOrDetail() {
      return this.mode === "edit" || this.mode === "detail";
    },
    isOtherPartnerType() {
      const selected = this.partnerTypeList.find(
        (p) => p.partner_type_id === this.form.partner_type_id,
      );
      const name = (selected?.partner_type_name || "").toLowerCase().trim();
      return name === "perjanjian lainnya" || name.includes("lainnya");
    },
    selectedPartnerTypeDivisionName() {
      const selected = this.partnerTypeList.find(
        (p) => p.partner_type_id === this.form.partner_type_id,
      );
      return selected?.mst_division_name || "-";
    },
    pageTitle() {
      return {
        add: "Add Dokumen",
        edit: "Edit Dokumen",
        detail: "Detail Dokumen",
      }[this.mode];
    },
    pageDescription() {
      return {
        add: "Tambah dokumen perjanjian kerja sama.",
        edit: "Edit dokumen perjanjian kerja sama.",
        detail: "Detail dokumen perjanjian kerja sama.",
      }[this.mode];
    },
    submitLabel() {
      return this.mode === "add" ? "Save" : "Update";
    },
    isStatusInactive() {
      const inactiveValue = this.statusList.find((s) =>
        s.description?.toLowerCase().includes("inactive"),
      )?.value;
      return this.form.status === inactiveValue;
    },
    isAddendum() {
      const desc =
        this.descriptionList
          .find((dl) => dl.value === this.form.description)
          ?.description?.toLowerCase() || "";
      return desc.includes("adendum") || desc.includes("amend");
    },
    isDetailComplete() {
      return this.detail.progress_status_name
        ?.toLowerCase()
        .includes("complete");
    },
    canEditWhenComplete() {
      const rt = String(this.detail?.renewal_type_name || "")
        .toLowerCase()
        .trim();
      return rt === "manual";
    },

    isDocumentInactiveOrSuperseded() {
      const d = this.detail || {};
      if (!d || !d.document_id) return false;

      const expired = String(d.expired_status || "")
        .toUpperCase()
        .trim();
      if (
        expired === "SUPERSEDED" ||
        expired === "INACTIVE" ||
        expired.includes("SUPERSEDE")
      ) {
        return true;
      }

      const statusRaw = d.status;
      if (statusRaw === 0 || statusRaw === "0" || Number(statusRaw) === 0) {
        return true;
      }

      const statusName = String(d.status_name || "")
        .toLowerCase()
        .trim();
      if (
        statusName === "inactive" ||
        statusName === "superseded" ||
        statusName.includes("inactive") ||
        statusName.includes("supersede")
      ) {
        return true;
      }

      if (d.inactive_reason != null && d.inactive_reason !== "") {
        return true;
      }

      return false;
    },

    isDetailEditLocked() {
      if (this.mode === "detail" && !this.detail?.document_id) return true;
      if (this.isDocumentInactiveOrSuperseded) return true;
      if (this.isDetailComplete && !this.canEditWhenComplete) return true;
      return false;
    },

    detailEditLockTitle() {
      if (this.isDocumentInactiveOrSuperseded) {
        const expired = String(this.detail?.expired_status || "").toUpperCase();
        if (expired === "SUPERSEDED") {
          return "Tidak bisa diedit (diganti Adendum)";
        }
        return "Tidak bisa diedit (Inactive)";
      }
      if (this.isDetailComplete && !this.canEditWhenComplete) {
        return "Tidak bisa diedit (Complete + AUTO)";
      }
      return "Edit";
    },
    isManualCompleteExtend() {
      return (
        this.mode === "edit" &&
        this.originalProgressStatusName?.toLowerCase().includes("complete") &&
        this.originalRenewalTypeName?.toLowerCase() === "manual"
      );
    },
    documentTypeName() {
      const selected = this.descriptionList.find(
        (d) => d.value === this.form.description,
      );
      return selected?.description || "Addendum";
    },
    routeKey() {
      return `${this.mode}:${this.$route.params.id || ""}`;
    },
    baseDescriptionLabel() {
      return this.descriptionList[0]?.description || "Perjanjian Kerja Sama";
    },
    /** Dari menu SPK Penawaran → Buat SPK: kunci keterangan = Surat Perintah Kerja */
    isSpkForced() {
      return (
        this.mode === "add" &&
        !this.form.ref_id &&
        !this.$route.query.ref_id &&
        String(this.$route.query.keterangan || "").toLowerCase() === "spk"
      );
    },
    /** Tipe dokumen utama saja (bukan adendum/amend) */
    mainDescriptionList() {
      return (this.descriptionList || []).filter((dl) => {
        const t = (dl.description || "").toLowerCase();
        return (
          !t.includes("adendum") &&
          !t.includes("addendum") &&
          !t.includes("amend")
        );
      });
    },
    lockedDescriptionLabel() {
      const found = (this.descriptionList || []).find(
        (d) => String(d.value) === String(this.form.description),
      );
      return found?.description || this.baseDescriptionLabel;
    },
    isCorporateLocked() {
      if (this.mode === "edit") return true;
      if (this.mode === "add")
        return (
          !!this.$route.query.corporate_id ||
          !!this.form.ref_id ||
          !!this.form.prev_document_id ||
          !!this.$route.query.prev_document_id
        );
      return false;
    },

    /** SPK atau adendum SPK aktif + Complete → boleh lanjut ke PKS */
    canContinueToPks() {
      if (this.mode !== "detail") return false;
      if (!this.isDetailComplete) return false;
      if (this.isDocumentInactiveOrSuperseded) return false;

      const desc = String(this.detail?.description_name || "").toLowerCase();
      if (desc.includes("perjanjian kerja")) return false;

      // SPK langsung
      if (desc.includes("surat perintah")) return true;

      // Adendum: hanya jika rantai masih SPK (bukan PKS)
      if (desc.includes("adendum") || desc.includes("amend")) {
        const parentDesc = String(
          this.parentDetail?.description_name || "",
        ).toLowerCase();
        if (parentDesc.includes("perjanjian kerja")) return false;
        if (parentDesc.includes("surat perintah")) return true;

        if (this.timelineList?.length) {
          const hasPks = this.timelineList.some((i) =>
            String(i.description_name || "")
              .toLowerCase()
              .includes("perjanjian kerja"),
          );
          if (hasPks) return false;
          return this.timelineList.some((i) =>
            String(i.description_name || "")
              .toLowerCase()
              .includes("surat perintah"),
          );
        }
        return false;
      }
      return false;
    },
    riwayatRows() {
      const currentId =
        Number(this.detail?.document_id) ||
        Number(this.$route?.params?.id) ||
        null;

      const rows = [];
      const seen = new Set();

      const push = (item) => {
        if (!item) return;
        const id = Number(item.document_id ?? item.id);
        if (!id || seen.has(id)) return;
        seen.add(id);
        rows.push({
          document_id: id,
          description_name: item.description_name || item.description || "-",
          document_number: item.document_number || "-",
          status_name: item.status_name || "",
          is_current: currentId != null && Number(currentId) === id,
        });
      };

      let list = this.timelineList;
      if (list && !Array.isArray(list) && Array.isArray(list.data)) {
        list = list.data;
      }
      if (Array.isArray(list)) {
        for (const i of list) push(i);
      }

      if (!rows.length) {
        if (this.parentDetail) push(this.parentDetail);
        else if (this.detail?.ref_id) {
          push({
            document_id: this.detail.ref_id,
            description_name: "Dokumen Induk",
            document_number: this.refDocumentNumber || "-",
          });
        }
        if (Array.isArray(this.addendumList)) {
          for (const item of this.addendumList) push(item);
        }
      }

      // Dokumen yang sedang dibuka WAJIB muncul (mis. PKS id 25)
      if (currentId && !seen.has(Number(currentId))) {
        push({
          document_id: currentId,
          description_name:
            this.detail?.description_name || this.detail?.description || "-",
          document_number: this.detail?.document_number || "-",
        });
      }

      return rows;
    },

    checklistLocalStatus() {
      if (!this.checklist.items?.length) {
        return {
          is_complete: this.checklist.is_complete,
          missing: this.checklist.missing,
        };
      }
      const missing = this.checklist.items.filter(
        (i) => i.required && !i.file_path && !this.checklistPendingFiles[i.key],
      );
      return {
        is_complete: missing.length === 0,
        missing: missing.map((i) => i.label),
      };
    },

    hasMainFile() {
      return this.form.file_path instanceof File || !!this.form.existing_file;
    },
    /** Ada file kontrak berformat PDF (file baru yang dipilih, atau file lama di server) */
    hasPdfMainFile() {
      if (this.form.file_path instanceof File) {
        return /\.pdf$/i.test(this.form.file_path.name || "");
      }
      return String(this.form.existing_file || "")
        .split("|")
        .some((k) => /\.pdf$/i.test(k.trim()));
    },
    completeProgressValue() {
      return this.progressList.find((r) =>
        r.description?.toLowerCase().includes("complete"),
      )?.value;
    },
    /** Progress masih Review → file kontrak hanya DOC/DOCX */
    isProgressReview() {
      const fromForm = this.progressList.find(
        (r) => String(r.value) === String(this.form.progress_status),
      )?.description;
      const name = (
        fromForm ||
        this.originalProgressStatusName ||
        this.detail?.progress_status_name ||
        ""
      ).toLowerCase();
      return name.includes("review");
    },
    mainFileAccept() {
      return this.isProgressReview ? ".doc,.docx" : ".pdf";
    },
    mainFileHint() {
      return this.isProgressReview
        ? "Drag & drop file di sini, atau klik untuk upload DOC/DOCX (maks 10MB)"
        : "Drag & drop file di sini, atau klik untuk upload PDF (maks 10MB)";
    },
    inactiveReasonOptions() {
      const list = (this.inactiveReasonList || []).map((r) => ({
        value: String(r.value),
        description: r.description,
      }));
      list.push({ value: "__other__", description: "Lainnya (ketik sendiri)" });
      return list;
    },

    isInactiveReasonOther() {
      return this.inactiveReasonSelect === "__other__";
    },
    isContinuation() {
      return this.mode === "add" && !!this.form.prev_document_id;
    },
    canContinueToSpk() {
      return this.canContinueToPks;
    },
    prevCandidateOptions() {
      return (this.prevCandidates || []).map((d) => ({
        value: d.document_id,
        label: `${d.description_name || "Dokumen"} — ${d.document_number}${
          d.end_date ? " (s/d " + d.end_date + ")" : ""
        }`,
      }));
    },
  },

  watch: {
    "$route.query.ref_id": {
      immediate: true,
      async handler(newRefId) {
        if (this.mode !== "add") return;
        const refId = Number(newRefId);
        if (!refId) return;
        if (!this.descriptionList.length) await this.loadMasterData();
        this.form.ref_id = refId;
        await this.loadParentDataForAddendum(refId);
      },
    },

    "form.progress_status"(newVal, oldVal) {
      if (!this.isFormReady || this.mode === "detail") return;

      const goingComplete = newVal === this.completeProgressValue;

      const fileMismatch =
        this.form.file_path instanceof File &&
        !this.isMainFileFormatValid(this.form.file_path);

      if (!goingComplete) {
        if (fileMismatch) {
          this.form.file_path = null;
          Swal.fire(
            "Info",
            "Format file tidak sesuai progress baru (Review: DOC/DOCX, selain itu: PDF). Silakan upload ulang.",
            "info",
          );
        }
        return;
      }

      Swal.fire({
        icon: "warning",
        title: "Konfirmasi Complete",
        text: this.hasPdfMainFile
          ? "Dokumen tidak bisa diubah lagi setelah diset Complete. Lanjutkan?"
          : "Progress Complete wajib memakai file PDF. Upload file PDF sebelum menyimpan. Dokumen tidak bisa diubah lagi setelah Complete. Lanjutkan?",
        showCancelButton: true,
        confirmButtonText: "Ya",
        cancelButtonText: "Batal",
      }).then((result) => {
        if (!result.isConfirmed) {
          this.$nextTick(() => {
            this.form.progress_status = oldVal ?? "";
          });
        } else if (fileMismatch) {
          this.form.file_path = null;
        }
      });
    },

    "form.renewal_type"(newVal, oldVal) {
      if (!this.isFormReady || this.mode === "detail") return;
      const autoValue = this.renewalList.find((r) =>
        r.description?.toLowerCase().includes("auto"),
      )?.value;
      if (newVal !== autoValue) return;

      Swal.fire({
        icon: "warning",
        title: "Konfirmasi Auto Renewal",
        text: "Dokumen ini akan diperbarui otomatis setelah tanggal selesai. Lanjutkan?",
        showCancelButton: true,
        confirmButtonText: "Ya, set Auto",
        cancelButtonText: "Batal",
      }).then((result) => {
        if (!result.isConfirmed) this.form.renewal_type = oldVal;
      });
    },
    "form.status"(newVal, oldVal) {
      if (!this.isFormReady || this.mode === "detail") return;

      const inactiveValue = this.statusList.find((s) =>
        s.description?.toLowerCase().includes("inactive"),
      )?.value;

      if (newVal !== inactiveValue) {
        this.form.inactive_reason = null;
        this.form.inactive_reason_other = "";
        this.inactiveReasonSelect = null;
        return;
      }

      // Bangun options: parameter + "Lainnya"
      const inputOptions = this.inactiveReasonList.reduce((acc, r) => {
        acc[String(r.value)] = r.description;
        return acc;
      }, {});
      inputOptions["__other__"] = "Lainnya (ketik sendiri)";

      Swal.fire({
        icon: "warning",
        title: "Alasan Dokumen Inactive",
        input: "select",
        inputOptions,
        inputPlaceholder: "Pilih alasan",
        showCancelButton: true,
        confirmButtonText: "Lanjut",
        cancelButtonText: "Batal",
        inputValidator: (value) =>
          !value ? "Alasan wajib dipilih" : undefined,
      }).then(async (result) => {
        if (!result.isConfirmed) {
          this.$nextTick(() => {
            this.form.status = oldVal ?? "";
          });
          return;
        }

        // Pilih dari dropdown parameter
        if (result.value !== "__other__") {
          this.form.inactive_reason = result.value;
          this.inactiveReasonSelect = result.value;
          this.form.inactive_reason_other = "";
          return;
        }

        // Pilih "Lainnya" → minta free text
        const { value: customText, isConfirmed } = await Swal.fire({
          icon: "warning",
          title: "Alasan Lainnya",
          input: "textarea",
          inputPlaceholder: "Ketik alasan inactive...",
          inputAttributes: {
            maxlength: 255,
          },
          showCancelButton: true,
          confirmButtonText: "Simpan",
          cancelButtonText: "Batal",
          inputValidator: (value) => {
            if (!value || !value.trim()) return "Alasan wajib diisi";
            if (value.trim().length > 255) return "Maksimal 255 karakter";
            return undefined;
          },
        });

        if (isConfirmed && customText?.trim()) {
          this.form.inactive_reason = customText.trim();
          this.inactiveReasonSelect = "__other__";
          this.form.inactive_reason_other = customText.trim();
        } else {
          this.$nextTick(() => {
            this.form.status = oldVal ?? "";
            this.form.inactive_reason = null;
            this.inactiveReasonSelect = null;
            this.form.inactive_reason_other = "";
          });
        }
      });
    },

    "form.description"(newVal) {
      if (!this.isFormReady) return;
      if (newVal === this.descriptionList[0]?.value) this.form.ref_id = null;
    },

    "form.mst_corporate_id"(newVal) {
      if (this.mode === "add" && newVal) this.loadChecklistTemplate();
    },

    routeKey: {
      async handler() {
        this.isLoading = true;
        this.isFormReady = false;
        try {
          if (this.isEditOrDetail) {
            await this.loadDetail();
          } else {
            this.resetFormState();
            if (this.$route.query.corporate_id) {
              this.form.mst_corporate_id = Number(
                this.$route.query.corporate_id,
              );
            }
            if (this.$route.query.transport_offer_id) {
              this.form.transport_offer_id = Number(
                this.$route.query.transport_offer_id,
              );
            }
            if (this.$route.query.prev_document_id) {
              await this.loadContinuationFromPrevious(
                Number(this.$route.query.prev_document_id),
              );
            }
            if (this.$route.query.ref_id) {
              this.form.ref_id = Number(this.$route.query.ref_id);
            } else {
              this.applyDefaultDescription();
            }
          }
        } finally {
          this.isLoading = false;
          this.isFormReady = true;
        }
      },
    },
    "form.partner_type_id"(newVal) {
      if (!this.isFormReady) return;
      if (!this.isOtherPartnerType) {
        this.form.partner_type_other = "";
      }

      const selected = this.partnerTypeList.find(
        (p) => p.partner_type_id === newVal,
      );
      this.form.mst_division_id = selected?.mst_division_id ?? null;
    },
    inactiveReasonSelect(newVal) {
      if (!this.isFormReady) return;

      if (newVal === "__other__") {
        // user pilih Lainnya → form.inactive_reason diisi dari input text
        this.form.inactive_reason =
          this.form.inactive_reason_other?.trim() || null;
      } else if (newVal) {
        this.form.inactive_reason = newVal;
        this.form.inactive_reason_other = "";
      } else {
        this.form.inactive_reason = null;
        this.form.inactive_reason_other = "";
      }
    },

    "form.inactive_reason_other"(val) {
      if (!this.isFormReady) return;
      if (this.isInactiveReasonOther) {
        this.form.inactive_reason = val?.trim() || null;
      }
    },
  },

  methods: {
    getEmptyForm() {
      return {
        transport_offer_id: null,
        ref_id: null,
        prev_document_id: null,
        mst_corporate_id: null,
        mst_division_id: null,
        partner_type_id: null,
        partner_type_other: null,
        company: null,
        document_number: "",
        start_date: "",
        end_date: "",
        description: "",
        status: 1,
        inactive_reason: null,
        inactive_reason_other: "",
        progress_status: "",
        renewal_type: "",
        file_path: null,
        existing_file: null,
      };
    },
    getEmptyChecklist() {
      return { company_type: "PT", items: [], is_complete: false, missing: [] };
    },
    resetFormState() {
      Object.assign(this.form, this.getEmptyForm());
      this.inactiveReasonSelect = null;
      this.detail = {};
      this.parentDetail = null;
      this.addendumList = [];
      this.timelineList = [];
      this.checklist = this.getEmptyChecklist();
      this.checklistPendingFiles = {};
      this.filteredDescriptionList = [];
      this.refDocumentNumber = "";
    },

    applyDefaultDescription() {
      if (this.mode !== "add") return;
      if (this.$route.query.ref_id || this.form.ref_id) return;
      if (!this.descriptionList?.length) return;

      const ket = String(this.$route.query.keterangan || "").toLowerCase();
      const byIncludes = (s) =>
        this.descriptionList.find((d) =>
          (d.description || "").toLowerCase().includes(s),
        );

      if (ket === "spk") {
        const spk =
          byIncludes("surat perintah") ||
          this.descriptionList.find((d) => String(d.value) === "2");
        if (spk) this.form.description = spk.value;
        return;
      }

      if (ket === "pks") {
        const pks = byIncludes("perjanjian kerja") || this.descriptionList[0];
        if (pks) this.form.description = pks.value;
        return;
      }

      const pks = byIncludes("perjanjian kerja") || this.descriptionList[0];
      if (pks) this.form.description = pks.value;
    },

    applyAddendumFilter() {
      this.filteredDescriptionList = this.descriptionList.filter((dl) => {
        const desc = dl.description?.toLowerCase() || "";
        return desc.includes("adendum") || desc.includes("amend");
      });
    },

    async loadMasterData() {
      const results = await Promise.allSettled([
        corporateController.getAll({ limit: 0, status: 1 }),
        partnerTypeController.getAll({ limit: 0 }),
        parameterController.getAll({ param_id: 5, limit: 0, is_active: 1 }),
        parameterController.getAll({ param_id: 1, limit: 0 }),
        parameterController.getAll({ param_id: 2, limit: 0, is_active: 1 }),
        parameterController.getAll({ param_id: 3, limit: 0 }),
        parameterController.getAll({ param_id: 11, limit: 0, is_active: 1 }),
      ]);

      const [
        corporate,
        partnerType,
        description,
        status,
        progress,
        renewal,
        inactiveReason,
      ] = results;

      if (corporate.status === "fulfilled") {
        this.corporateList = [
          ...new Map(
            corporate.value.data.map((item) => [item.mst_corporate_id, item]),
          ).values(),
        ];
        this.companyList = this.corporateList
          .filter((item) => [2, 2062].includes(item.mst_corporate_id))
          .map((item) => ({
            ...item,
            mst_corporate_alias:
              item.mst_corporate_id === 2
                ? "FSLI"
                : item.mst_corporate_id === 2062
                ? "FSWI"
                : item.mst_corporate_name,
          }));
      } else {
        console.warn("Corporate gagal dimuat:", corporate.reason);
      }

      if (partnerType.status === "fulfilled") {
        this.partnerTypeList = [
          ...new Map(
            partnerType.value.data.map((item) => [item.partner_type_id, item]),
          ).values(),
        ];
      }
      if (description.status === "fulfilled")
        this.descriptionList = description.value.data;
      if (status.status === "fulfilled") this.statusList = status.value.data;
      if (progress.status === "fulfilled")
        this.progressList = progress.value.data;
      if (renewal.status === "fulfilled") this.renewalList = renewal.value.data;
      if (inactiveReason.status === "fulfilled") {
        this.inactiveReasonList = inactiveReason.value.data;
      } else {
        console.warn("Alasan inactive gagal dimuat:", inactiveReason.reason);
      }
    },

    async loadDetail() {
      try {
        const id = this.$route.params.id;
        const res = await monitoringController.getById(id);

        if (this.mode === "detail") {
          const row = Array.isArray(res) ? res[0] : res;
          this.detail = row || {};
          if (!this.detail.document_id && id) {
            this.detail.document_id = Number(id);
          }
          await this.loadAddendumList(id);
          if (this.detail.ref_id) {
            await this.loadParentDetail(this.detail.ref_id);
          }
          this.originalPrevDocumentId = res.prev_document_id ?? null;
          await this.loadPrevCandidates(id);
          await this.loadChecklist(id);
          await this.loadTimeline(id);
          return;
        }

        const isSuperseded =
          String(res.status) === "0" ||
          String(res.expired_status || "").toUpperCase() === "SUPERSEDED" ||
          String(res.status_name || "")
            .toLowerCase()
            .trim() === "inactive";
        if (isSuperseded) {
          await Swal.fire({
            icon: "warning",
            title: "Tidak dapat diedit",
            text:
              res.inactive_reason ||
              "Dokumen sudah digantikan oleh Adendum dan tidak dapat diedit.",
            confirmButtonText: "Lihat Detail",
          });
          this.$router.replace(`/detail-document/${id}`);
          return;
        }

        this.originalProgressStatusName = res.progress_status_name || "";
        this.originalRenewalTypeName = res.renewal_type_name || "";

        Object.assign(this.form, {
          transport_offer_id: res.transport_offer_id ?? null,
          ref_id: res.ref_id,
          prev_document_id: res.prev_document_id ?? null,
          mst_corporate_id: res.mst_corporate_id,
          mst_division_id: res.mst_division_id,
          company: res.company,
          partner_type_id: res.partner_type_id,
          partner_type_other: res.partner_type_other || "",
          document_number: res.document_number,
          start_date: res.start_date ? res.start_date.split("T")[0] : "",
          end_date: res.end_date ? res.end_date.split("T")[0] : "",
          description: res.description,
          status: res.status,
          inactive_reason: res.inactive_reason ?? null,
          progress_status: res.progress_status,
          renewal_type: res.renewal_type,
          existing_file: res.file_path,
          file_path: null,
        });
        // Sinkronkan dropdown alasan inactive
        const reason =
          res.inactive_reason != null ? String(res.inactive_reason) : null;
        const foundInParam = (this.inactiveReasonList || []).some(
          (r) => String(r.value) === reason,
        );

        if (reason && foundInParam) {
          this.inactiveReasonSelect = reason;
          this.form.inactive_reason = reason;
          this.form.inactive_reason_other = "";
        } else if (reason) {
          // free-text lama
          this.inactiveReasonSelect = "__other__";
          this.form.inactive_reason = reason;
          this.form.inactive_reason_other = reason;
        } else {
          this.inactiveReasonSelect = null;
          this.form.inactive_reason = null;
          this.form.inactive_reason_other = "";
        }

        if (res.ref_id) {
          this.applyAddendumFilter();
          try {
            const parent = await monitoringController.getById(res.ref_id);
            this.refDocumentNumber = parent?.document_number || "";
          } catch {
            this.refDocumentNumber = "";
          }
        } else {
          this.filteredDescriptionList = [];
        }

        this.checklistPendingFiles = {};
        await this.loadChecklist(id);
      } catch (error) {
        console.error(error);
        Swal.fire("Error", "Gagal memuat data dokumen", "error");
      }
    },

    async loadAddendumList(refId) {
      try {
        const list = await monitoringController.getAddendumList(refId);
        this.addendumList = Array.isArray(list) ? list : [];
      } catch (error) {
        console.error(error);
        this.addendumList = [];
      }
    },

    async loadParentDetail(parentId) {
      try {
        this.parentDetail = await monitoringController.getById(parentId);
      } catch (error) {
        console.error(error);
        this.parentDetail = null;
      }
    },

    async loadTimeline(documentId) {
      try {
        const raw = await monitoringController.getTimeline(documentId);
        let list = raw;
        if (list && !Array.isArray(list) && Array.isArray(list.data)) {
          list = list.data;
        }
        this.timelineList = Array.isArray(list) ? list : [];
        console.log(
          "[timeline]",
          documentId,
          "count=",
          this.timelineList.length,
          "ids=",
          this.timelineList.map((x) => x.document_id),
        );
      } catch (e) {
        console.error(e);
        this.timelineList = [];
      }
    },

    goToAddAddendum() {
      if (!this.isDetailComplete) {
        Swal.fire({
          icon: "warning",
          title: "Tidak Bisa Menambahkan Addendum",
          text: "Progress dokumen harus Complete terlebih dahulu sebelum menambahkan addendum atau amandemen.",
        });
        return;
      }
      const parentId = this.detail.ref_id || this.detail.document_id;
      this.$router.push({
        path: "/add-document",
        query: { ref_id: parentId, from: this.$route.fullPath },
      });
    },

    goToContinuePks() {
      if (!this.canContinueToPks) {
        Swal.fire({
          icon: "warning",
          title: "Tidak dapat lanjut ke PKS",
          text: "Dokumen harus Complete dan masih aktif (SPK / adendum SPK).",
        });
        return;
      }
      this.$router.push({
        path: "/add-document",
        query: {
          prev_document_id: this.detail.document_id,
          corporate_id: this.detail.mst_corporate_id,
          keterangan: "pks",
          from: this.$route.fullPath,
        },
      });
    },

    goToEdit(id) {
      if (this.isDetailEditLocked) {
        Swal.fire({
          icon: "warning",
          title: "Tidak dapat diedit",
          text: this.detailEditLockTitle,
        });
        return;
      }
      this.$router.push(`/edit-document/${id}`);
    },

    goBack() {
      this.$router.push(this.$route.query.from || "/monitoring-document");
    },

    async loadParentDataForAddendum(refId) {
      try {
        const parent = await monitoringController.getById(refId);
        this.form.mst_corporate_id = parent.mst_corporate_id;
        this.form.company = parent.company;
        this.form.partner_type_id = parent.partner_type_id;
        this.form.partner_type_other = parent.partner_type_other || "";
        this.form.renewal_type = parent.renewal_type;
        this.refDocumentNumber = parent.document_number;
        this.form.mst_division_id = parent.mst_division_id;

        if (this.mode === "edit") {
          this.applyAddendumFilter();
          return;
        }

        const existing = await monitoringController.getAddendumList(refId);

        const countByKeyword = (keyword) =>
          existing.filter((d) =>
            this.descriptionList
              .find((dl) => dl.value === d.description)
              ?.description?.toLowerCase()
              .includes(keyword),
          ).length;

        const nextAdendum = countByKeyword("adendum") + 1;
        const nextAmend = countByKeyword("amend") + 1;

        this.filteredDescriptionList = this.descriptionList.filter((dl) => {
          const desc = dl.description?.toLowerCase() || "";
          if (desc.includes("adendum"))
            return dl.description.includes(`${nextAdendum}`);
          if (desc.includes("amend"))
            return dl.description.includes(`${nextAmend}`);
          return false;
        });

        if (this.filteredDescriptionList.length) {
          this.form.description = this.filteredDescriptionList[0].value;
        }
      } catch (error) {
        console.error(error);
      }
    },

    async loadChecklist(documentId) {
      this.checklist = await documentChecklistController.getChecklist(
        documentId,
      );
    },

    async loadChecklistTemplate() {
      if (!this.form.mst_corporate_id) return;
      try {
        this.checklist =
          await documentChecklistController.getChecklistByCorporate(
            this.form.mst_corporate_id,
          );
      } catch (error) {
        console.error(error);
      }
    },

    handleChecklistUpload(key, event) {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (file) this.stageChecklistFile(key, file);
    },

    handleChecklistDrop(key, event) {
      this.checklistDragging = null;
      const file = event.dataTransfer.files?.[0];
      if (file) this.stageChecklistFile(key, file);
    },

    /** Dokumen legalitas: PDF / JPG / JPEG / PNG */
    stageChecklistFile(key, file) {
      const name = (file.name || "").toLowerCase();
      const type = file.type || "";
      const isAllowed =
        type === "application/pdf" ||
        type === "image/jpeg" ||
        type === "image/jpg" ||
        type === "image/png" ||
        /\.(pdf|jpe?g|png)$/.test(name);
      if (!isAllowed) {
        Swal.fire(
          "Warning",
          "Dokumen legalitas harus berformat PDF, JPG, JPEG, atau PNG",
          "warning",
        );
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        Swal.fire("Warning", "Ukuran file maksimal 10MB", "warning");
        return;
      }
      this.checklistPendingFiles = {
        ...this.checklistPendingFiles,
        [key]: file,
      };
    },

    cancelPendingChecklistFile(key) {
      const updated = { ...this.checklistPendingFiles };
      delete updated[key];
      this.checklistPendingFiles = updated;
    },

    triggerChecklistInput(key) {
      const ref = this.$refs["checklistInput_" + key];
      const el = Array.isArray(ref) ? ref[0] : ref;
      el?.click();
    },

    async uploadPendingChecklistFiles() {
      const entries = Object.entries(this.checklistPendingFiles);
      if (!entries.length) return;

      for (const [key, file] of entries) {
        if (this.mode === "add") {
          if (!this.form.mst_corporate_id)
            throw new Error("Pilih Nama Perusahaan terlebih dahulu");
          await documentChecklistController.uploadFileByCorporate(
            this.form.mst_corporate_id,
            key,
            file,
          );
        } else {
          await documentChecklistController.uploadFile(
            this.$route.params.id,
            key,
            file,
          );
        }
      }

      this.checklistPendingFiles = {};
      if (this.mode === "add") await this.loadChecklistTemplate();
      else await this.loadChecklist(this.$route.params.id);
    },

    async removeChecklistFile(key) {
      const result = await Swal.fire({
        icon: "warning",
        title: "Hapus File?",
        text: "File checklist ini akan dihapus.",
        showCancelButton: true,
        confirmButtonText: "Ya, Hapus",
        cancelButtonText: "Batal",
      });
      if (!result.isConfirmed) return;

      try {
        if (this.mode === "add") {
          if (!this.form.mst_corporate_id) {
            Swal.fire(
              "Warning",
              "Pilih Nama Perusahaan terlebih dahulu",
              "warning",
            );
            return;
          }
          await documentChecklistController.deleteFileByCorporate(
            this.form.mst_corporate_id,
            key,
          );
          this.clearChecklistFileLocal(key);
          await this.loadChecklistTemplate();
        } else {
          const documentId = this.$route.params.id;
          if (!documentId) {
            Swal.fire("Error", "ID dokumen tidak ditemukan", "error");
            return;
          }
          await documentChecklistController.deleteFile(documentId, key);
          this.clearChecklistFileLocal(key);
          await this.loadChecklist(documentId);
        }
        this.showToast("File berhasil dihapus", "success");
      } catch (error) {
        console.error(error);
        Swal.fire(
          "Error",
          error.response?.data?.message || "Gagal menghapus file checklist",
          "error",
        );
      }
    },

    clearChecklistFileLocal(key) {
      if (!this.checklist.items?.length) return;

      this.checklist.items = this.checklist.items.map((i) =>
        i.key === key ? { ...i, file_path: null } : i,
      );

      const requiredMissing = this.checklist.items.filter(
        (i) => i.required && !i.file_path,
      );
      this.checklist.is_complete = requiredMissing.length === 0;
      this.checklist.missing = requiredMissing.map((i) => i.label);
    },

    isMainFileFormatValid(file) {
      const name = (file?.name || "").toLowerCase();
      return this.isProgressReview
        ? /\.(doc|docx)$/.test(name)
        : name.endsWith(".pdf");
    },

    validateFile(file) {
      if (!file) return false;
      const name = (file.name || "").toLowerCase();
      const type = file.type || "";

      if (this.isProgressReview) {
        const isDoc =
          type === "application/msword" ||
          type ===
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
          name.endsWith(".doc") ||
          name.endsWith(".docx");
        if (!isDoc) {
          Swal.fire(
            "Warning",
            "Progress masih Review: file harus berformat DOC/DOCX (bukan PDF)",
            "warning",
          );
          return false;
        }
      } else {
        const isPdf = type === "application/pdf" || name.endsWith(".pdf");
        if (!isPdf) {
          Swal.fire("Warning", "File harus berformat PDF", "warning");
          return false;
        }
      }

      if (file.size > 10 * 1024 * 1024) {
        Swal.fire("Warning", "Ukuran file maksimal 10MB", "warning");
        return false;
      }
      return true;
    },

    handleFileUpload(event) {
      const file = event.target.files[0] || null;
      if (file && this.validateFile(file)) this.form.file_path = file;
      event.target.value = "";
    },

    handleDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files[0] || null;
      if (file && this.validateFile(file)) this.form.file_path = file;
    },

    removeSelectedFile() {
      this.form.file_path = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },

    formatFileSize(bytes) {
      if (!bytes) return "";
      const kb = bytes / 1024;
      return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(1)} MB`;
    },

    fileIconClass(fileName) {
      const name = String(fileName || "")
        .split("|")[0]
        .toLowerCase();
      if (/\.(jpe?g|png)$/.test(name))
        return "bi-file-earmark-image-fill text-primary";
      if (/\.(docx?)$/.test(name))
        return "bi-file-earmark-word-fill text-primary";
      return "bi-file-earmark-pdf-fill text-danger";
    },

    async getFileUrl(fileKey) {
      if (!fileKey) return "#";
      try {
        const firstKey = fileKey.split("|")[0];
        const res = await monitoringController.getSignedUrl(firstKey);
        return res.url;
      } catch (err) {
        console.error(err);
        return "#";
      }
    },

    getFileName(filePath) {
      if (!filePath) return "File Tidak Ditemukan";
      return filePath.split("/").pop().split("\\").pop();
    },

    async downloadFile(fileKey) {
      if (!fileKey) return;
      try {
        const url = await this.getFileUrl(fileKey);
        if (url && url !== "#") window.open(url, "_blank");
        else Swal.fire("Error", "Gagal mendapatkan link download", "error");
      } catch {
        Swal.fire("Error", "Gagal mengunduh file", "error");
      }
    },

    buildFormData() {
      const payload = new FormData();
      const skipKeys = [
        "existing_file",
        "file_path",
        "refDocumentNumber",
        "inactive_reason_other",
      ];

      Object.keys(this.form).forEach((key) => {
        if (skipKeys.includes(key)) return;
        if (this.form[key] !== null && this.form[key] !== undefined) {
          payload.append(key, this.form[key]);
        }
      });

      if (this.form.file_path instanceof File) {
        payload.append("file_path", this.form.file_path);
      } else if (this.form.existing_file) {
        payload.append("existing_file", this.form.existing_file);
      }

      return payload;
    },

    async saveData() {
      if (this.form.document_number) {
        this.form.document_number = this.form.document_number
          .toUpperCase()
          .trim();
      }

      const requiredFields = {
        mst_corporate_id: "Nama Perusahaan",
        document_number: "Nomor Dokumen",
        start_date: "Tanggal Mulai",
        end_date: "Tanggal Selesai",
        description: "Keterangan",
        progress_status: "Progress Dokumen",
        renewal_type: "Renewal Type",
      };

      const missing = Object.entries(requiredFields)
        .filter(([field]) => !this.form[field])
        .map(([, label]) => label);

      if (missing.length) {
        Swal.fire({
          icon: "warning",
          title: "Lengkapi semua data sebelum menyimpan",
          html: `Data yang belum diisi:<br><strong>${missing.join(
            ", ",
          )}</strong>`,
        });
        return;
      }

      if (
        this.form.progress_status === this.completeProgressValue &&
        !this.isManualCompleteExtend &&
        !this.hasPdfMainFile
      ) {
        Swal.fire({
          icon: "warning",
          title: "Lengkapi Data",
          text: "Progress Complete membutuhkan File Kontrak / SPK / PO berformat PDF. Silakan upload file PDF terlebih dahulu.",
        });
        return;
      }
      if (this.isOtherPartnerType && !this.form.partner_type_other?.trim()) {
        Swal.fire({
          icon: "warning",
          title: "Lengkapi Data",
          text: "Mohon isi keterangan Tipe Partner untuk pilihan 'Perjanjian Lainnya'",
        });
        return;
      }

      if (
        this.form.start_date &&
        this.form.end_date &&
        new Date(this.form.end_date) < new Date(this.form.start_date)
      ) {
        Swal.fire(
          "Warning",
          "Tanggal selesai tidak boleh lebih kecil dari tanggal mulai",
          "warning",
        );
        return;
      }
      const inactiveValue = this.statusList.find((s) =>
        s.description?.toLowerCase().includes("inactive"),
      )?.value;

      if (this.form.status === inactiveValue) {
        if (this.isInactiveReasonOther) {
          if (!this.form.inactive_reason_other?.trim()) {
            Swal.fire({
              icon: "warning",
              title: "Lengkapi Data",
              text: "Ketik alasan inactive terlebih dahulu.",
            });
            return;
          }
          this.form.inactive_reason = this.form.inactive_reason_other.trim();
        } else if (!this.form.inactive_reason) {
          Swal.fire({
            icon: "warning",
            title: "Lengkapi Data",
            text: "Pilih alasan inactive terlebih dahulu.",
          });
          return;
        }
      }

      if (this.isAddendum && !this.form.ref_id) {
        Swal.fire(
          "Warning",
          "Pilih dokumen induk untuk addendum ini",
          "warning",
        );
        return;
      }

      this.isLoading = true;

      Swal.fire({
        title: "Menyimpan Dokumen...",
        html: "Mohon tunggu, jangan tutup atau refresh halaman ini.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        await this.uploadPendingChecklistFiles();
        const payload = this.buildFormData();

        if (this.mode === "add") {
          await monitoringController.create(payload);
          Swal.close();
          this.showToast("Dokumen berhasil ditambahkan", "success");
          this.goBack();
        } else {
          await monitoringController.update(this.$route.params.id, payload);
          Swal.close();
          Swal.fire({
            icon: "success",
            title: "Berhasil",
            text: "Dokumen berhasil diperbarui",
          });
          await this.loadDetail();
        }
      } catch (error) {
        console.error(error);
        Swal.close();
        Swal.fire({
          icon: "error",
          title: "Gagal",
          text:
            error.response?.data?.message || "Terjadi kesalahan saat menyimpan",
        });
      } finally {
        this.isLoading = false;
      }
    },

    formatDate(date) {
      if (!date) return "-";
      return new Intl.DateTimeFormat("id-ID", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      }).format(new Date(date));
    },

    getStatusClass(status) {
      if (!status) return "secondary";
      const s = status.toLowerCase();
      if (s.includes("inactive")) return "status-danger";
      if (s.includes("active")) return "status-success";
      return "status-danger";
    },

    getExpiredClass(status) {
      if (!status) return "secondary";
      if (status === "EXPIRED" || status === "EXPIRED TODAY") return "danger";
      if (status === "SOON EXPIRED") return "warning";
      return "success";
    },

    getDaysText(days) {
      if (days == null || isNaN(Number(days))) return 0;
      return Math.abs(Number(days));
    },

    shouldShowDays(item) {
      return (
        item?.days_until_expired != null &&
        !isNaN(item.days_until_expired) &&
        item.days_until_expired <= 60
      );
    },

    showToast(message, type = "success") {
      if (this.toastTimer) clearTimeout(this.toastTimer);
      this.toast = { show: true, message, type };
      this.toastTimer = setTimeout(() => {
        this.toast.show = false;
      }, 3000);
    },

    async loadPrevCandidates(id) {
      try {
        this.prevCandidates = await monitoringController.getPrevCandidates(id);
      } catch (e) {
        console.error(e);
        this.prevCandidates = [];
      }
    },
    async loadContinuationFromPrevious(prevId) {
      try {
        this.form.prev_document_id = Number(prevId);
        const prev = await monitoringController.getById(prevId);
        if (!prev) return;
        this.form.mst_corporate_id = prev.mst_corporate_id;
        this.form.company = prev.company;
        this.form.partner_type_id = prev.partner_type_id;
        this.form.partner_type_other = prev.partner_type_other || "";
        this.form.mst_division_id = prev.mst_division_id;
        this.form.renewal_type = prev.renewal_type;
        this.refDocumentNumber = prev.document_number || "";
      } catch (e) {
        console.error(e);
      }
    },

    goToContinueSpk() {
      if (!this.canContinueToSpk) {
        Swal.fire({
          icon: "warning",
          title: "Tidak dapat lanjut ke SPK",
          text: "Dokumen harus Complete dan masih aktif (SPK / adendum SPK).",
        });
        return;
      }
      this.$router.push({
        path: "/add-document",
        query: {
          prev_document_id: this.detail.document_id,
          corporate_id: this.detail.mst_corporate_id,
          keterangan: "spk",
          from: this.$route.fullPath,
        },
      });
    },
  },

  beforeUnmount() {
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },

  async mounted() {
    await this.loadMasterData();

    if (this.isEditOrDetail) {
      await this.loadDetail();
    }

    if (this.mode === "add") {
      if (this.$route.query.corporate_id) {
        this.form.mst_corporate_id = Number(this.$route.query.corporate_id);
      }
      if (this.$route.query.transport_offer_id) {
        this.form.transport_offer_id = Number(
          this.$route.query.transport_offer_id,
        );
      }
      if (this.$route.query.ref_id) {
        this.form.ref_id = Number(this.$route.query.ref_id);
      }
      if (this.$route.query.prev_document_id) {
        await this.loadContinuationFromPrevious(
          Number(this.$route.query.prev_document_id),
        );
      }
      this.applyDefaultDescription();
    }

    this.isFormReady = true;
  },
};
